import { useState } from "react";

import { FeatureFlagControl } from "@/Common/Components/FeatureFlagControl";
import { Modal } from "@/Common/Components/Modals/Modal";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { FaxDirectorySelectorComponent } from "@/ContactDirectory/Components/FaxDirectorySelectorComponent";
import { DirectoryFaxUpdateOptions } from "@/ContactDirectory/Models/DirectoryFaxUpdateOptions";
import { FaxAndSignatureClinicianSelection, FaxAndSignatureClinicianSelectionState } from "@/ExportableFile/Components/FaxAndSignatureClinicianSelection";
import { FaxCoverSheetEditorSection } from "@/ExportableFile/Components/FaxCoverSheetEditorSection";
import { IFaxCoverSheetProps } from "@/ExportableFile/Components/IFaxInformationProps";

import styles from "./PortfolioResendFaxPopup.module.scss";

interface IPortfolioResendFaxPopupProps {
    popupTitle?: string;
    showX?: boolean;
    containsCoverSheet?: boolean;
    initialRecipientName?: string;
    initialFaxNumber?: string;
    onCancel?: () => void;
    onSubmit?: (faxDetails: IFaxDetails, onCompleted?: () => void) => void;
}

interface IFaxDetails {
    faxNumber: string;
    displayName: string;
    phoneNumber: string;
    pdfCoverPageParams: IFaxCoverSheetProps
}

function PortfolioResendFaxPopup(props: Readonly<IPortfolioResendFaxPopupProps>): JSX.Element {
    const initialRecipientName = props.initialRecipientName ?? "--";
    const { getText } = useTranslation();
    const [destinatorInfoState, setDestinatorInfoState] = useState<FaxAndSignatureClinicianSelectionState>(null);
    const [isFaxValid, setIsFaxValid] = useState<boolean>(false);
    const [directoryFaxInfoState, setDirectoryFaxInfoState] = useState<DirectoryFaxUpdateOptions>(null);
    const [pdfCoverPageParams, setPdfCoverPageParams] = useState<IFaxCoverSheetProps>({});
    const [isSendButtonEnabled, setIsSendButtonEnabled] = useState<boolean>(true);
    const [showCoverSheetValidationErrors, setShowCoverSheetValidationErrors] = useState<boolean>(false);
    const [isCoverSheetSubjectValid, setIsCoverSheetSubjectValid] = useState<boolean>(true);
    const [isCoverSheetMessageValid, setIsCoverSheetMessageValid] = useState<boolean>(true);

    const isDirectoryNameValid = !IsNullOrWhiteSpace(destinatorInfoState?.CustomClinicianName ?? directoryFaxInfoState?.description);
    const isSelectedFaxNumberValid = (cacheStore.ClinicSettings.EnableContactDirectory ? isFaxValid : true);
    const isDirectoryFaxNumberValid = !IsNullOrWhiteSpace(destinatorInfoState?.CustomClinicianFaxNumber ?? directoryFaxInfoState?.faxNumber);

    const isFaxAllowed = isDirectoryFaxNumberValid && isDirectoryNameValid && isSelectedFaxNumberValid;

    function onFaxClick(): void {
        setIsSendButtonEnabled(false);

        if (pdfCoverPageParams?.GenerateCoverPage && (!isCoverSheetMessageValid || !isCoverSheetSubjectValid)) {
            setShowCoverSheetValidationErrors(true);
            setIsSendButtonEnabled(true);
            return;
        }

        const phoneNumber = directoryFaxInfoState?.selectedEntity?.getRawPhoneNumber() ?? destinatorInfoState?.CustomClinicianPhoneNumber;
        const faxNumber = directoryFaxInfoState?.faxNumber ?? destinatorInfoState?.CustomClinicianFaxNumber;
        const displayName = directoryFaxInfoState?.description ?? destinatorInfoState?.CustomClinicianName;

        const faxDetails: IFaxDetails = {
            faxNumber,
            displayName,
            phoneNumber,
            pdfCoverPageParams
        };

        props.onSubmit?.(faxDetails, () => setIsSendButtonEnabled(true));
    }

    const footer: JSX.Element = (
        <div className={styles.footer}>
            <button
                className="btn btn-default"
                onClick={() => props.onCancel?.()}
            >
                {getText("CancelLabel")}
            </button>
            <button
                className="btn btn-primary"
                onClick={() => onFaxClick()}
                disabled={!isFaxAllowed || !isSendButtonEnabled}
            >
                {getText("FaxLabel")}
            </button>
        </div>
    );

    return (
        <Modal
            centerContent
            title={props.popupTitle ?? getText("SendFaxPopupLabel")}
            showX={props.showX}
            width={500}
            showOverlay
            draggable
            onClose={() => props.onCancel?.()}
            footer={footer}
            overlayZIndex={99}
            maxHeight="550px"
        >
            <FeatureFlagControl
                flag={cacheStore.ClinicSettings.EnableContactDirectory}
                activeFeature={(
                    <div className="margin-lg-bottom">
                        <FaxDirectorySelectorComponent
                            onFaxUpdate={option => setDirectoryFaxInfoState(option)}
                            isFaxValid={isValid => setIsFaxValid(isValid)}
                            defaultSelectedRecipient={props.initialFaxNumber ? initialRecipientName : ""}
                            defaultSelectedFaxNumber={props.initialFaxNumber}
                        />
                    </div>
                )}
                fallbackFeature={(
                    <FaxAndSignatureClinicianSelection
                        useLabsSearch
                        useNameAndFaxOnly
                        StateUpdateDelegate={selectionState => setDestinatorInfoState(selectionState)}
                    />
                )}
            />

            {cacheStore.ClinicSettings.EnableFaxCoverSheet && (
                <FaxCoverSheetEditorSection
                    containsCoverSheet={props.containsCoverSheet}
                    containerClassName="margin-md-bottom"
                    subject={pdfCoverPageParams.CoverPageTitle}
                    message={pdfCoverPageParams.CoverPageContent}
                    isSubjectValid={showCoverSheetValidationErrors ? isCoverSheetSubjectValid : true}
                    isMessageValid={showCoverSheetValidationErrors ? isCoverSheetMessageValid : true}
                    validate={(isSubjectValid, isMessageValid) => {
                        setIsCoverSheetSubjectValid(isSubjectValid);
                        setIsCoverSheetMessageValid(isMessageValid);
                    }}
                    onCheckboxChange={value => {
                        setShowCoverSheetValidationErrors(false);
                        setPdfCoverPageParams(prev => ({ ...prev, GenerateCoverPage: value }));
                    }}
                    onSubjectChange={value => {
                        setShowCoverSheetValidationErrors(false);
                        setPdfCoverPageParams(prev => ({ ...prev, CoverPageTitle: value }));
                    }}
                    onMessageChange={value => {
                        setShowCoverSheetValidationErrors(false);
                        setPdfCoverPageParams(prev => ({ ...prev, CoverPageContent: value }));
                    }}
                />
            )}
        </Modal>
    );
}

export { PortfolioResendFaxPopup };
